import React, { useMemo } from 'react';
import { type Asset } from 'types/asset.types';
import { type MakeModelDetails } from 'types/makeModel.types';
import { PNG } from 'components/Asset/PNG';
import { getImageName } from 'utils/helpers/mapAssetImage';
import { handleAssetStatus } from 'views/Settings/ExternalUsers/AssetView/helpers/common';
import { timeAgo } from 'views/FleetOverview/components/AssetCardsV2/utils';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { useNavigate } from 'react-router-dom';
import {
  AssetCard,
  CardHeader,
  CardHeaderLeft,
  AssetImageBox,
  AssetIdentityBox,
  BumperID,
  StatusChip,
  Badge,
  CardDivider,
  MetricItem,
  MetricLabel,
  MetricValue,
  PanelTitle,
  EmptyState,
  AssetSyncLabel,
  AssetStateLabel,
  CardHeaderRight,
  TirePressureContainer,
  LocationBadge,
  LocationValue,
  WorkshopDuration,
  TPMSSection,
  TPMSSchematicContainer,
  TPMSPlaceholder,
  TPMSStatusLabel,
  AlertsRow,
  AlertsLabel,
  AlertChip,
  ShiftAlertTicker,
  ShiftAlertName,
  ShiftMetricsRow,
  FuelProgressWrapper,
  FuelProgressBarInner,
  FuelProgressFill,
  FuelProgressLabel,
  FuelAlertChip,
} from '../styles';
import { ControlRoomAssetGrid } from '../components/ControlRoomAssetGrid';
import { getLocationColor } from 'utils/helpers/locationColors';
import TirePressureSchematic from 'components/TirePressure/TirePressureSchematic';
import {
  convertBasedOnUnitSystem,
  convertCentToFahr,
  convertKmsToMiles,
  convertMetricTonsToImperialTons,
} from 'utils/helpers/unitSystem';
import { isDataInShift } from 'utils/shift';
import { toUnixTimestamp } from '../utils';
import { useTranslation } from 'react-i18next';

export interface FleetLiveStatusPanelProps {
  filteredAssets: Asset[];
  latestAvailableData: Record<string, any>;
  unitSystem: string;
  enableDarkTheme: boolean;
  assetsLoaded: boolean;
  startTs?: string;
  endTs?: string;
  makeModelDetails?: MakeModelDetails[];
}

export const FleetLiveStatusPanel = ({
  filteredAssets,
  latestAvailableData,
  unitSystem,
  enableDarkTheme,
  assetsLoaded,
  startTs,
  endTs,
  makeModelDetails = [],
}: FleetLiveStatusPanelProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tickerIndex, setTickerIndex] = React.useState(0);

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTickerIndex((prev) => prev + 1);
    }, 4000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const makeModelMap = useMemo(() => {
    const map = new Map<string, MakeModelDetails>();
    for (const m of makeModelDetails) {
      if (m.make && m.model) map.set(`${m.make}|${m.model}`, m);
    }
    return map;
  }, [makeModelDetails]);

  const haulers = useMemo(() => {
    return filteredAssets.filter((a: Asset) =>
      (a.assetType || '').toLowerCase().includes('truck')
    );
  }, [filteredAssets]);

  const loaders = useMemo(() => {
    return filteredAssets.filter((a: Asset) =>
      (a.assetType || '').toLowerCase().includes('loader')
    );
  }, [filteredAssets]);

  const haulersOnlineCount = useMemo(() => {
    return haulers.filter((a: Asset) => {
      const deviceId = a.device?.deviceId;
      if (!deviceId) return false;
      const opData = latestAvailableData[deviceId];
      return opData?.ignitionOn === true;
    }).length;
  }, [haulers, latestAvailableData]);

  const loadersOnlineCount = useMemo(() => {
    return loaders.filter((a: Asset) => {
      const deviceId = a.device?.deviceId;
      if (!deviceId) return false;
      const opData = latestAvailableData[deviceId];
      return opData?.ignitionOn === true;
    }).length;
  }, [loaders, latestAvailableData]);

  if (assetsLoaded && filteredAssets.length === 0) {
    return (
      <>
        <PanelTitle $enableDarkTheme={enableDarkTheme}>
          {t('fleet_status')}
        </PanelTitle>
        <EmptyState $enableDarkTheme={enableDarkTheme}>
          {t('no_asset_data_available')}
        </EmptyState>
      </>
    );
  }

  const renderAsset = (asset: Asset) => {
    const deviceId = asset.device?.deviceId ?? '';

    const opData =
      latestAvailableData[deviceId] ||
      latestAvailableData[deviceId.toLowerCase()] ||
      latestAvailableData[deviceId.toUpperCase()] ||
      {};

    const isAvailable = handleAssetStatus(asset.isActive) === 'available';
    const isIgnitionOn = !!opData.ignitionOn;
    const png = getImageName(asset, 'Small');
    const derivedState = (opData.derived_state || '').toLowerCase();
    const latestState = isAvailable ? derivedState || '—' : 'OFFLINE';

    const utilization = opData?.utilization ?? null;
    const trips = opData?.tripCount ?? null;
    const tph = opData?.tonsPerHour ?? null;
    const isTruck = (asset.assetType || '').toLowerCase().includes('truck');

    const opDataIsCurrent =
      !opData.engineStartTime ||
      isDataInShift(opData.engineStartTime, startTs, endTs);

    // Freshness Guard: If data is older than 1 hour, we hide real-time sensors
    const lastSyncTs = opData?.latestTs ?? 0;
    const isDataStale = lastSyncTs > 0 && Date.now() - lastSyncTs > 3600000;

    const fuelPercent = isDataStale ? null : opData?.fuelLevel ?? null;
    // fuel analysis fields are independent of sensor staleness
    const fuelLeftPercent = opData?.fuelLeftPercent ?? null;
    const fuelAlertLevel = opData?.fuelAlertLevel ?? null;

    const greaserRaw = opData?.greaserLevel ?? null;
    const greaserActive = greaserRaw !== null && greaserRaw !== undefined;
    const greaserOk = greaserActive && Number(greaserRaw) === 0;

    const backendActiveAlerts = opData?.activeAlerts ?? {};
    const backendShiftAlerts = opData?.shiftAlerts ?? {};

    const dtcCount = backendActiveAlerts?.dtcs?.length ?? 0;
    const ruleAlertCount = backendActiveAlerts?.ruleNotifications?.length ?? 0;
    const tpmsAlertCount = backendActiveAlerts?.tpmsNotifications?.length ?? 0;

    const shiftAlertNames = [
      ...(backendShiftAlerts?.dtcs ?? []).map((a: any) => a?.displayMessage),
      ...(backendShiftAlerts?.ruleNotifications ?? []).map(
        (a: any) => a?.displayMessage
      ),
      ...(backendShiftAlerts?.tpmsNotifications ?? []).map(
        (a: any) => a?.displayMessage
      ),
    ].filter(Boolean);

    const activeShiftAlert =
      shiftAlertNames.length > 0
        ? shiftAlertNames[tickerIndex % shiftAlertNames.length]
        : null;

    const lastLocation = Object.prototype.hasOwnProperty.call(
      opData,
      'last_location'
    )
      ? opData.last_location
      : asset.device?.last_location;
    const workshopDuration = (() => {
      if (lastLocation?.toLowerCase() !== 'workshop') return null;
      const durationSec = Number(
        opData?.last_location_duration ?? asset.device?.last_location_duration
      );
      if (!durationSec || isNaN(durationSec) || durationSec <= 0) return null;
      const mins = Math.floor(durationSec / 60);
      const minUnit = t('duration_min_short');
      const hrUnit = t('duration_hr_short');
      if (mins < 60) return `${mins} ${minUnit}`;
      const hrs = Math.floor(mins / 60);
      const rem = mins % 60;
      return rem > 0
        ? `${hrs} ${hrUnit} ${rem} ${minUnit}`
        : `${hrs} ${hrUnit}`;
    })();

    return (
      <AssetCard
        key={asset.assetId}
        $available={isAvailable}
        $ignitionOn={isIgnitionOn}
        $enableDarkTheme={enableDarkTheme}
      >
        <CardHeader>
          <CardHeaderLeft>
            <AssetImageBox $enableDarkTheme={enableDarkTheme}>
              <PNG
                name={png}
                style={{
                  width: '64px',
                  height: '64px',
                  objectFit: 'contain',
                }}
              />
            </AssetImageBox>
            <Badge
              $variant={isIgnitionOn ? 'success' : 'error'}
              $enableDarkTheme={enableDarkTheme}
            >
              {isTruck ? t('std_asset_hauler') : t('std_asset_loader')}{' '}
              {isIgnitionOn ? t('ignition_on') : t('ignition_off')}
            </Badge>
            <AssetSyncLabel $enableDarkTheme={enableDarkTheme}>
              {(() => {
                const ts = opData?.latestTs;
                return ts ? timeAgo(ts) : 'N/A';
              })()}
            </AssetSyncLabel>
          </CardHeaderLeft>

          <AssetIdentityBox>
            <BumperID $enableDarkTheme={enableDarkTheme}>
              {asset.bumperNumber || deviceId}
            </BumperID>

            {latestState != null && latestState !== '' && (
              <AssetStateLabel $enableDarkTheme={enableDarkTheme}>
                {(() => {
                  if (latestState === '—') return '—';
                  const cleanKey = String(latestState).toLowerCase().trim();
                  const stateKey = `state_${cleanKey}`;
                  return t(stateKey, {
                    defaultValue: t(cleanKey, {
                      defaultValue: cleanKey.replace(/_/g, ' '),
                    }),
                  });
                })()}
              </AssetStateLabel>
            )}
            {lastLocation && (
              <LocationBadge
                $color={getLocationColor(lastLocation)}
                $enableDarkTheme={enableDarkTheme}
              >
                <LocationValue $color={getLocationColor(lastLocation)}>
                  {lastLocation}
                  {workshopDuration && (
                    <WorkshopDuration $enableDarkTheme={enableDarkTheme}>
                      {t('workshop_duration_for')} {workshopDuration}
                    </WorkshopDuration>
                  )}
                </LocationValue>
              </LocationBadge>
            )}

            {(fuelLeftPercent !== null || fuelPercent !== null) && (
              <FuelProgressWrapper>
                <LocalGasStationIcon
                  sx={{
                    fontSize: '1.2rem',
                    color: enableDarkTheme
                      ? 'rgba(255,255,255,0.7)'
                      : 'rgba(0,0,0,0.7)',
                  }}
                />
                <FuelProgressBarInner $enableDarkTheme={enableDarkTheme}>
                  <FuelProgressFill
                    $fillPercent={fuelLeftPercent ?? fuelPercent}
                    $enableDarkTheme={enableDarkTheme}
                  />
                </FuelProgressBarInner>
                <FuelProgressLabel $enableDarkTheme={enableDarkTheme}>
                  {Math.round(fuelLeftPercent ?? fuelPercent ?? 0)}%
                </FuelProgressLabel>
              </FuelProgressWrapper>
            )}

            {fuelAlertLevel !== null && (
              <FuelAlertChip
                $level={fuelAlertLevel}
                $enableDarkTheme={enableDarkTheme}
              >
                {t(`state_${String(fuelAlertLevel).toLowerCase()}`, {
                  defaultValue: t(String(fuelAlertLevel).toLowerCase(), {
                    defaultValue: fuelAlertLevel,
                  }),
                })}
              </FuelAlertChip>
            )}
          </AssetIdentityBox>

          <CardHeaderRight>
            <StatusChip
              $available={isAvailable}
              $enableDarkTheme={enableDarkTheme}
            >
              {isAvailable ? t('available_status') : t('out_of_service_status')}
            </StatusChip>
            {opData?.lastStatus?.status &&
              (() => {
                const rawStatus = String(opData.lastStatus.status);
                const s = rawStatus.toLowerCase();
                const variant =
                  rawStatus === 'down'
                    ? 'error'
                    : rawStatus === 'ready'
                    ? 'success'
                    : 'warning';
                const statusKey = `state_${s}`;
                return (
                  <Badge $variant={variant} $enableDarkTheme={enableDarkTheme}>
                    {t(statusKey, {
                      defaultValue: t(s, { defaultValue: rawStatus }),
                    })}
                  </Badge>
                );
              })()}
          </CardHeaderRight>
        </CardHeader>

        <CardDivider $enableDarkTheme={enableDarkTheme} />

        <AlertsRow>
          <AlertsLabel $enableDarkTheme={enableDarkTheme}>
            {t('alerts')}
          </AlertsLabel>

          <AlertChip
            $variant="dtc"
            $hasAlerts={dtcCount > 0}
            $enableDarkTheme={enableDarkTheme}
            onClick={() => {
              navigate('/diagnostic-trouble-codes', {
                state: { data: { bumperNumber: asset.bumperNumber || '' } },
              });
            }}
          >
            <span>●</span>
            <span>{dtcCount} DTC</span>
          </AlertChip>

          <AlertChip
            $variant="rule"
            $hasAlerts={ruleAlertCount > 0}
            $enableDarkTheme={enableDarkTheme}
            onClick={() => {
              navigate('/notifications', {
                state: { data: { bumperNumber: asset.bumperNumber || '' } },
              });
            }}
          >
            <span>●</span>
            <span>
              {ruleAlertCount} {t('rule_word')}
            </span>
          </AlertChip>

          {asset.tpmsEnabled && (
            <AlertChip
              $variant="tpms"
              $hasAlerts={tpmsAlertCount > 0}
              $enableDarkTheme={enableDarkTheme}
              onClick={() => {
                navigate('/tire-analytics/notification', {
                  state: { data: { bumperNumber: asset.bumperNumber || '' } },
                });
              }}
            >
              <span>●</span>
              <span>{tpmsAlertCount} TPMS</span>
            </AlertChip>
          )}
        </AlertsRow>

        <ShiftAlertTicker>
          {shiftAlertNames.length > 0 ? (
            <ShiftAlertName $visible={Boolean(activeShiftAlert)}>
              {shiftAlertNames.length > 1 &&
                `(${(tickerIndex % shiftAlertNames.length) + 1}/${
                  shiftAlertNames.length
                }) `}
              {activeShiftAlert}
            </ShiftAlertName>
          ) : (
            <ShiftAlertName $visible={true} $isFallback={true}>
              {t('no_current_shift_triggered_alerts')}
            </ShiftAlertName>
          )}
        </ShiftAlertTicker>

        <CardDivider $enableDarkTheme={enableDarkTheme} />

        <ShiftMetricsRow>
          {(() => {
            interface MetricItemData {
              label: string | null;
              value: any;
              show?: boolean;
              athenaComputed?: boolean;
            }

            const isMetric = unitSystem === 'metric';

            const row1: MetricItemData[] = [
              {
                label: t('dm_kpi_utilization'),
                value:
                  utilization && Math.round(utilization) > 0
                    ? `${Math.round(utilization)}%`
                    : '—',
                athenaComputed: true,
              },
              {
                label: t('speed'),
                value: (() => {
                  if (!isIgnitionOn || isDataStale) return '—';
                  const speed = opData?.speed ?? 0;
                  if (speed <= 0) return '0' + (isMetric ? ' km/h' : ' mph');

                  return isMetric
                    ? `${Math.round(speed)} km/h`
                    : `${Math.round(convertKmsToMiles(speed))} mph`;
                })(),
              },
              {
                label: t('idle_time'),
                athenaComputed: true,
                value: (() => {
                  const cur = isIgnitionOn
                    ? Math.round(opData?.currentIdling ?? 0)
                    : 0;
                  const cum = Math.round(opData?.cumulativeIdling ?? 0);
                  if (!opDataIsCurrent || !cum) return '—';
                  return (
                    <>
                      <span>{cur ? `${cur}m` : '—'}</span>
                      <span
                        style={{
                          fontSize: 10,
                          opacity: 0.5,
                          fontWeight: 500,
                          marginLeft: 4,
                        }}
                      >
                        ({cum ? `${cum}m` : '—'})
                      </span>
                    </>
                  );
                })(),
              },
            ];

            const row2: MetricItemData[] = [
              {
                label: t('payload'),
                value: (() => {
                  if (!opDataIsCurrent) return '—';

                  const sensorPayload =
                    isIgnitionOn && !isDataStale
                      ? Number(opData?.currentPayload ?? 0)
                      : 0;

                  const curRaw = sensorPayload > 0 ? sensorPayload : null;

                  const cumRaw = opData?.cumulativePayload ?? null;

                  const cur =
                    curRaw !== null
                      ? Math.round(
                          isMetric
                            ? curRaw
                            : convertMetricTonsToImperialTons(curRaw)
                        )
                      : null;
                  const cum =
                    cumRaw !== null ? Math.round(Number(cumRaw)) : null;

                  if (cur === null && (cum === null || !cum)) return '—';

                  return (
                    <>
                      <span>{cur !== null ? `${cur}T` : '—'}</span>
                      <span
                        style={{
                          fontSize: 10,
                          opacity: 0.5,
                          fontWeight: 500,
                          marginLeft: 4,
                        }}
                      >
                        ({cum ? `${cum}T` : '—'})
                      </span>
                    </>
                  );
                })(),
                show: isTruck,
              },
              {
                label: t('haul_dist'),
                value: (() => {
                  if (!opDataIsCurrent) return '—';
                  const curRaw =
                    isIgnitionOn && !isDataStale
                      ? opData?.currentHaulDistance ?? 0
                      : null;
                  const cumRaw = opData?.cumulativeHaulDistance ?? null;

                  const cur =
                    curRaw !== null ? Math.round(Number(curRaw)) : null;
                  const cum =
                    cumRaw !== null ? Math.round(Number(cumRaw)) : null;

                  if (cur === null && cum === null) return '—';
                  return (
                    <>
                      <span>
                        {cur !== null
                          ? `${cur}${isMetric ? ' km' : ' mi'}`
                          : '—'}
                      </span>
                      <span
                        style={{
                          fontSize: 10,
                          opacity: 0.5,
                          fontWeight: 500,
                          marginLeft: 4,
                        }}
                      >
                        {cum && cum > 0
                          ? `(${cum}${isMetric ? ' km' : ' mi'})`
                          : ''}
                      </span>
                    </>
                  );
                })(),
                show: isTruck,
                athenaComputed: true,
              },
              {
                label: t('cycle_time_sigma'),
                value: (() => {
                  const cumRaw = opData?.cumulativeCycleTime ?? null;
                  const tripCount = opData?.tripCount ?? 0;
                  const avg =
                    cumRaw !== null && tripCount > 0
                      ? Math.round(Number(cumRaw) / tripCount)
                      : null;
                  return (
                    <>
                      <span>{avg !== null ? `${avg}m` : '—'}</span>
                      <span
                        style={{
                          fontSize: 10,
                          opacity: 0.5,
                          fontWeight: 500,
                          marginLeft: 4,
                        }}
                      >
                        {cumRaw && Math.round(Number(cumRaw)) > 0
                          ? `(${Math.round(Number(cumRaw))}m)`
                          : ''}
                      </span>
                    </>
                  );
                })(),
                show: isTruck,
                athenaComputed: true,
              },
            ];

            const row3: MetricItemData[] = [
              {
                label: t('greaser_level'),
                value:
                  greaserActive && !isDataStale ? (
                    <span style={{ color: greaserOk ? '#4caf50' : '#f44336' }}>
                      {greaserOk ? 'OK' : 'LOW'}
                    </span>
                  ) : (
                    '—'
                  ),
              },
              {
                label: t('eng_temp'),
                value: (() => {
                  const engineTemp = opData?.engineTemp;
                  if (
                    engineTemp === null ||
                    typeof engineTemp === 'undefined' ||
                    Number.isNaN(Number(engineTemp)) ||
                    isDataStale
                  )
                    return '—';

                  const convertedTemp = convertBasedOnUnitSystem(
                    convertCentToFahr,
                    Number(engineTemp),
                    unitSystem,
                    0
                  );

                  const unitLabel = unitSystem === 'metric' ? '°C' : '°F';

                  return `${convertedTemp}${unitLabel}`;
                })(),
              },
              {
                label: t('tons_per_hour'),
                value:
                  tph && Math.round(tph) > 0 ? String(Math.round(tph)) : '—',
                show: isTruck,
                athenaComputed: true,
              },
            ];

            const row4: MetricItemData[] = [
              {
                label: t('total_trips'),
                value: trips && trips > 0 ? String(trips) : '—',
                show: isTruck,
                athenaComputed: true,
              },
            ];

            const items: MetricItemData[] = [
              ...row1,
              ...row3,
              ...row2,
              ...row4,
            ];

            return items.map((m, idx) => {
              const isVisible = m.show !== false && m.label !== null;
              return (
                <MetricItem
                  key={idx}
                  style={{
                    minWidth: 0,
                    opacity: isVisible ? 1 : 0,
                    pointerEvents: isVisible ? 'auto' : 'none',
                  }}
                >
                  {m.label && (
                    <MetricLabel
                      $enableDarkTheme={enableDarkTheme}
                      title={
                        m.athenaComputed
                          ? t('control_room_algorithm_tooltip')
                          : undefined
                      }
                    >
                      {m.label}
                      {m.athenaComputed && (
                        <span
                          style={{
                            marginLeft: 2,
                            color: '#a78bfa',
                            fontSize: '1em',
                            fontWeight: 600,
                            opacity: 0.85,
                          }}
                        >
                          *
                        </span>
                      )}
                    </MetricLabel>
                  )}
                  {m.value !== null && (
                    <MetricValue $enableDarkTheme={enableDarkTheme}>
                      {m.value}
                    </MetricValue>
                  )}
                </MetricItem>
              );
            });
          })()}
        </ShiftMetricsRow>

        <CardDivider $enableDarkTheme={enableDarkTheme} />

        {asset.tpmsEnabled ? (
          <TPMSSection $enableDarkTheme={enableDarkTheme}>
            <TPMSSchematicContainer>
              <TirePressureContainer $enableDarkTheme={enableDarkTheme}>
                <TirePressureSchematic
                  asset={asset}
                  liveTagData={opData.tpmsTags || {}}
                  width={50}
                  maxHeight={50}
                />
              </TirePressureContainer>
            </TPMSSchematicContainer>
          </TPMSSection>
        ) : (
          <TPMSPlaceholder $enableDarkTheme={enableDarkTheme}>
            <TPMSStatusLabel
              $enabled={false}
              $enableDarkTheme={enableDarkTheme}
            >
              {t('tpms_not_configured')}
            </TPMSStatusLabel>
          </TPMSPlaceholder>
        )}
      </AssetCard>
    );
  };

  return (
    <ControlRoomAssetGrid
      haulers={haulers}
      loaders={loaders}
      haulersOnlineCount={haulersOnlineCount}
      loadersOnlineCount={loadersOnlineCount}
      renderLeftAsset={renderAsset}
      renderRightAsset={renderAsset}
      enableDarkTheme={enableDarkTheme}
      latestAvailableData={latestAvailableData}
    />
  );
};
